import React, { useState, useEffect } from "react";
import Sidebar from '../Sidebar/SidebarAdmin';
import { database } from "../../firebase";
import { ref, onValue, off, update, remove, set } from "firebase/database";
import {
  Box,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TextField,
  Stack,
  Button,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

const UserManagement = () => {
  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const toggleSidebar = () => setIsSidebarOpen(prev => !prev);
  const sidebarWidth = isSidebarOpen ? 250 : 80;

  // Data state
  const [users, setUsers] = useState([]);

  // Permanent filter/sort state
  const [filterQuery, setFilterQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortField, setSortField] = useState('registration_date');
  const [sortOrder, setSortOrder] = useState('asc');

  // Temp filter/sort states for dialog
  const [tempQuery, setTempQuery] = useState('');
  const [tempStart, setTempStart] = useState('');
  const [tempEnd, setTempEnd] = useState('');
  const [tempField, setTempField] = useState('registration_date');
  const [tempOrder, setTempOrder] = useState('asc');

  const [openFilterDialog, setOpenFilterDialog] = useState(false);

  // Edit user dialog state
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [current, setCurrent] = useState({ key: '', data: {} });

  // Fetch users from Firebase
  useEffect(() => {
    const usersRef = ref(database, 'users');
    onValue(usersRef, snap => {
      const data = snap.val() || {};
      setUsers(Object.entries(data));
    });
    return () => off(usersRef);
  }, []);

  // Apply filter settings
  const applyFilters = () => {
    setFilterQuery(tempQuery);
    setStartDate(tempStart);
    setEndDate(tempEnd);
    setSortField(tempField);
    setSortOrder(tempOrder);
    setOpenFilterDialog(false);
  };

  // Filtered list
  const filtered = users.filter(([key, user]) => {
    const text = `${user.first_name} ${user.last_name} ${user.email} ${user.organization}`.toLowerCase();
    const matches = !filterQuery || text.includes(filterQuery.toLowerCase());
    let inRange = true;
    if (startDate) inRange = new Date(user.registration_date) >= new Date(startDate);
    if (inRange && endDate) inRange = new Date(user.registration_date) <= new Date(endDate);
    return matches && inRange;
  });

  // Sorted list
  const sortedUsers = [...filtered].sort((a, b) => {
    const userA = a[1], userB = b[1];
    let valA, valB;
    switch (sortField) {
      case 'first_name':
        valA = userA.first_name.toLowerCase();
        valB = userB.first_name.toLowerCase();
        break;
      case 'registration_date':
        valA = new Date(userA.registration_date);
        valB = new Date(userB.registration_date);
        break;
      default:
        return 0;
    }
    if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
    if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  // Open edit dialog
  const handleEdit = (key, data) => {
    setCurrent({ key, data });
    setOpenEditDialog(true);
  };
  const closeEdit = () => setOpenEditDialog(false);

  // Save edits
  const saveEdit = () => {
    const { key, data } = current;
    update(ref(database, `users/${key}`), data);
    closeEdit();
  };

  // Ban user: move to banned node then remove
  const banUser = () => {
    const { key, data } = current;
    set(ref(database, `banned/${key}`), data).then(() => {
      remove(ref(database, `users/${key}`));
      closeEdit();
    });
  };

  return (
    <Box sx={{ backgroundColor: "#f7f9fc", minHeight: "100vh", pt: 8, position: 'relative' }}>
      <Sidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        sx={{
          position: 'fixed', top: '64px',
          left: { xs: '8px', sm: `calc(${sidebarWidth}px + 16px)` },
          right: { xs: '8px', sm: '16px' }, bottom: '16px',
          backgroundColor: '#cfe8fc', borderRadius: 2, p: { xs: 2, sm: 3 }, m: 0, overflow: 'hidden'
        }}
      >
        {/* Filters button */}
        <Button variant="contained" onClick={() => setOpenFilterDialog(true)} sx={{ mb: 2 }}>
          Filters
        </Button>

        {/* Filter Dialog */}
        <Dialog open={openFilterDialog} onClose={() => setOpenFilterDialog(false)} fullWidth>
          <DialogTitle>Filter & Sort Users</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Search"
                size="small"
                value={tempQuery}
                onChange={e => setTempQuery(e.target.value)}
                fullWidth
              />
              <TextField
                label="From"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={tempStart}
                onChange={e => setTempStart(e.target.value)}
                fullWidth
              />
              <TextField
                label="To"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={tempEnd}
                onChange={e => setTempEnd(e.target.value)}
                fullWidth
              />
              <Select
                size="small"
                value={tempField}
                onChange={e => setTempField(e.target.value)}
                fullWidth
              >
                <MenuItem value="registration_date">Registered</MenuItem>
                <MenuItem value="first_name">First Name</MenuItem>
                <MenuItem value="key">User ID</MenuItem>
              </Select>
              <Select
                size="small"
                value={tempOrder}
                onChange={e => setTempOrder(e.target.value)}
                fullWidth
              >
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenFilterDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={applyFilters}>Apply</Button>
          </DialogActions>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={openEditDialog} onClose={closeEdit} fullWidth>
          <DialogTitle>Edit User</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="First Name" value={current.data.first_name || ''} onChange={e => setCurrent(c => ({ ...c, data: { ...c.data, first_name: e.target.value } }))} fullWidth />
              <TextField label="Last Name" value={current.data.last_name || ''} onChange={e => setCurrent(c => ({ ...c, data: { ...c.data, last_name: e.target.value } }))} fullWidth />
              <TextField label="Email" value={current.data.email || ''} onChange={e => setCurrent(c => ({ ...c, data: { ...c.data, email: e.target.value } }))} fullWidth />
              <TextField label="Organization" value={current.data.organization || ''} onChange={e => setCurrent(c => ({ ...c, data: { ...c.data, organization: e.target.value } }))} fullWidth />
              <TextField label="Phone" value={current.data.phone || ''} onChange={e => setCurrent(c => ({ ...c, data: { ...c.data, phone: e.target.value } }))} fullWidth />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={banUser} color="error">Ban User</Button>
            <Button onClick={closeEdit}>Cancel</Button>
            <Button variant="contained" onClick={saveEdit}>Save</Button>
          </DialogActions>
        </Dialog>

        {/* Table label */}
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2, textAlign: 'center', fontSize: { xs: '1.25rem', sm: '1.75rem' } }}>
          Users Overview
        </Typography>

        {/* Table */}
        <TableContainer component={Paper} sx={{ height: 'calc(100% - 208px)', boxShadow: 'none' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', '& thead th': { backgroundColor: 'primary.main', color: '#fff', fontWeight: 'bold', py: 1, textAlign: 'center', fontSize: { xs: '0.75rem', sm: '1rem' } }, '& tbody td': { py: { xs: 0.5, sm: 1 }, px: 1, textAlign: 'center', whiteSpace: 'normal', wordBreak: 'break-word', minHeight: { xs: 32, sm: 48 } }, '& tbody tr:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Organization</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Registered</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedUsers.map(([key, user]) => (
                <TableRow key={key} hover>
                  <TableCell>{user.first_name} {user.last_name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.organization}</TableCell>
                  <TableCell>{user.phone}</TableCell>
                  <TableCell>{new Date(user.registration_date).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => handleEdit(key, user)}>Edit</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default UserManagement;
